/**
 * CourseCard — circular orange-soft icon badge, bold title, course code subtitle,
 * optional kebab (only when real actions exist) and optional progress (only when real data is passed).
 */
import React from "react";
import { Link } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { ActionPill } from "./ActionPill";

export interface CourseCardProps {
  to: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  chip?: React.ReactNode;
  /** Shown only when real completion data exists. */
  progress?: { label: string; done: number; total: number };
  /** Kebab menu is rendered only when this is provided. */
  onMenu?: () => void;
  menuLabel?: string;
  state?: unknown;
  className?: string;
}

export function CourseCard({ to, title, subtitle, icon, chip, progress, onMenu, menuLabel = "More actions", state, className = "" }: CourseCardProps) {
  const pct = progress && progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  return (
    <div
      className={`group relative flex min-w-0 flex-col gap-4 rounded-card border border-border bg-card p-5 shadow-soft transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-primary/50 motion-reduce:transition-none motion-reduce:hover:transform-none ${className}`.trim()}
    >
      <div className="flex items-start justify-between">
        {icon ? (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
            {icon}
          </span>
        ) : (
          <span />
        )}
        {onMenu && (
          <button
            type="button"
            onClick={onMenu}
            aria-label={menuLabel}
            className="relative z-10 -mr-2 -mt-2 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-soft focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MoreVertical size={18} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="min-w-0">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug text-foreground">
          <Link to={to} state={state} className="after:absolute after:inset-0 after:rounded-card focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring">
            {title}
          </Link>
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {subtitle && <span className="font-mono">{subtitle}</span>}
          {chip}
        </div>
      </div>

      {progress && (
        <div className="mt-auto space-y-1.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{progress.label}</span>
            <span>
              {progress.done}/{progress.total}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-track" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done} aria-label={progress.label}>
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      <div className="flex justify-end" aria-hidden="true">
        <ActionPill tone="ghost">Open</ActionPill>
      </div>
    </div>
  );
}
